import { DefaultChatTransport, type UIMessage } from 'ai';
import { fetchWithAuth } from '@/lib/api/fetch-with-auth';
import { ENV } from '@/lib/config';
import { withAgentUnavailableDetection } from '@/shared/lib/ai-stream';

export interface ReasonerTransportOptions {
  contextId: string;
  workspaceId: string;
  /**
   * Fields every message in this conversation sends, such as which agent answers and whether it
   * runs the draft. A send's own body wins over them.
   */
  body?: () => Record<string, unknown>;
}

function lastUserText(messages: readonly UIMessage[] | undefined): string {
  const last = [...(messages ?? [])].reverse().find((message) => message.role === 'user');
  return (
    last?.parts
      .filter((part): part is Extract<typeof part, { type: 'text' }> => part.type === 'text')
      .map((part) => part.text)
      .join('') ?? ''
  );
}

function plainHeaders(headers: HeadersInit | undefined): Record<string, string> {
  if (headers instanceof Headers) return Object.fromEntries(headers.entries());
  if (Array.isArray(headers)) return Object.fromEntries(headers);
  return headers ?? {};
}

/**
 * The transport for one reasoner conversation: its message endpoint, the platform's auth, the
 * selected workspace and the app's locale. A send without an explicit `input` takes its prompt from
 * the message being sent, so a chat that only calls `sendMessage({ text })` still reaches the agent.
 */
export function createReasonerTransport({
  contextId,
  workspaceId,
  body,
}: ReasonerTransportOptions) {
  return new DefaultChatTransport<UIMessage>({
    api: `${ENV.INFERENCE_ENGINE_API_BASE_URL}/api/v1/reasoner/contexts/${contextId}/message`,
    credentials: 'include',
    fetch: withAgentUnavailableDetection(fetchWithAuth),
    prepareSendMessagesRequest: (opts) => {
      const headers = plainHeaders(opts.headers);
      if (ENV.ENABLE_LOCAL_TESTING_AUTHENTICATION && ENV.LOCAL_TOKEN) {
        headers.Authorization = ENV.LOCAL_TOKEN;
      }
      const sent = (opts.body ?? {}) as Record<string, unknown>;
      return {
        api: opts.api,
        credentials: opts.credentials,
        body: {
          ...(body?.() ?? {}),
          ...sent,
          input: sent.input ?? { prompt: lastUserText(opts.messages) },
        },
        headers: {
          ...headers,
          'x-selected-workspace-id': workspaceId,
        },
      };
    },
  });
}
